import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, X, Settings2, Check } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { apiFetch } from '@/lib/api';
import { useAuthContext } from '@/context/AuthContext';
import { formatCents, formatDate, todayISO } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import { SortableTh } from '@/components/SortableTh';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ExpenseCategory {
  id: number;
  name: string;
  created_at: string;
}

interface ExpenseRow {
  id: number;
  category_id: number | null;
  category_name: string | null;
  date: string;
  amount: number;
  payee: string | null;
  notes: string | null;
  created_by: number;
  created_at: string;
}

interface ExpensesResponse {
  data: {
    expenses: ExpenseRow[];
    /** Amount SUM across the whole filtered set (drives the total display). */
    total: number;
    /** Matching ROW count across the whole filtered set (drives pagination). */
    count: number;
    page: number;
    limit: number;
    sort: string;
    dir: string;
  };
}

interface CategoriesResponse {
  data: { categories: ExpenseCategory[] };
}

// ─── Expense Modal (create / edit) ────────────────────────────────────────────

interface ExpenseModalProps {
  mode: 'create' | 'edit';
  expense?: ExpenseRow;
  categories: ExpenseCategory[];
  onClose: () => void;
  onSaved: () => void;
}

function ExpenseModal({ mode, expense, categories, onClose, onSaved }: ExpenseModalProps) {
  const todayIso = todayISO();
  const [categoryId, setCategoryId] = useState<string>(
    expense?.category_id != null ? String(expense.category_id) : '',
  );
  const [date, setDate] = useState(expense ? expense.date.slice(0, 10) : todayIso);
  const [amountStr, setAmountStr] = useState(
    expense ? String((expense.amount / 100).toFixed(2)) : '',
  );
  const [payee, setPayee] = useState(expense?.payee ?? '');
  const [notes, setNotes] = useState(expense?.notes ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!categoryId) {
      setError('Please select a category.');
      return;
    }
    const amount = Math.round(parseFloat(amountStr) * 100);
    if (!amountStr || isNaN(amount) || amount <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }

    setLoading(true);
    try {
      const body = {
        category_id: Number(categoryId),
        date,
        amount,
        payee: payee.trim() || undefined,
        notes: notes.trim() || undefined,
      };

      if (mode === 'create') {
        await apiFetch('/api/expenses', { method: 'POST', body: JSON.stringify(body) });
        toast.success('Expense recorded');
      } else {
        await apiFetch(`/api/expenses/${expense!.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        toast.success('Expense updated');
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">
            {mode === 'create' ? 'New Expense' : 'Edit Expense'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Category */}
          <div className="space-y-1.5">
            <Label htmlFor="ex-category">
              Category <span className="text-destructive">*</span>
            </Label>
            <select
              id="ex-category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={loading}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Select category…</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date + Amount */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ex-date">Date</Label>
              <Input
                id="ex-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ex-amount">
                Amount ($) <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                  $
                </span>
                <Input
                  id="ex-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amountStr}
                  onChange={(e) => setAmountStr(e.target.value)}
                  className="pl-7 tabular-nums"
                  required
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Payee */}
          <div className="space-y-1.5">
            <Label htmlFor="ex-payee">Payee</Label>
            <Input
              id="ex-payee"
              value={payee}
              onChange={(e) => setPayee(e.target.value)}
              placeholder="e.g. Home Depot"
              disabled={loading}
            />
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="ex-notes">Notes</Label>
            <textarea
              id="ex-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              rows={2}
              placeholder="Optional notes…"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : mode === 'create' ? 'Record Expense' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Delete Confirm Dialog ─────────────────────────────────────────────────────

interface DeleteExpenseDialogProps {
  expense: ExpenseRow;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
}

function DeleteExpenseDialog({ expense, onCancel, onConfirm, loading }: DeleteExpenseDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Delete Expense</h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Delete the{' '}
            <span className="font-semibold text-foreground">{formatCents(expense.amount)}</span>{' '}
            expense on{' '}
            <span className="font-semibold text-foreground">{formatDate(expense.date)}</span>? This
            cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={onConfirm} disabled={loading}>
              {loading ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Manage Categories Modal (Admin only) ──────────────────────────────────────

interface ManageCategoriesModalProps {
  categories: ExpenseCategory[];
  onClose: () => void;
  onChanged: () => void;
}

function ManageCategoriesModal({ categories, onClose, onChanged }: ManageCategoriesModalProps) {
  const [newName, setNewName] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (renamingId !== null) {
      renameInputRef.current?.focus();
    }
  }, [renamingId]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setAddLoading(true);
    try {
      await apiFetch('/api/expenses/categories', {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
      toast.success(`Category "${name}" added`);
      setNewName('');
      onChanged();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add category';
      toast.error(msg);
    } finally {
      setAddLoading(false);
    }
  }

  function startRename(cat: ExpenseCategory) {
    setRenamingId(cat.id);
    setRenameValue(cat.name);
  }

  async function handleRename(catId: number) {
    const name = renameValue.trim();
    if (!name) {
      setRenamingId(null);
      return;
    }
    setRenameLoading(true);
    try {
      await apiFetch(`/api/expenses/categories/${catId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      });
      toast.success('Category renamed');
      setRenamingId(null);
      onChanged();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to rename category';
      toast.error(msg);
    } finally {
      setRenameLoading(false);
    }
  }

  async function handleDelete(catId: number) {
    setDeletingId(catId);
    try {
      await apiFetch(`/api/expenses/categories/${catId}`, { method: 'DELETE' });
      toast.success('Category deleted');
      onChanged();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete category';
      toast.error(msg);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Manage Categories</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Category list */}
          <ul className="space-y-1 max-h-60 overflow-y-auto">
            {categories.length === 0 && (
              <li className="text-sm text-muted-foreground py-2 text-center">No categories yet.</li>
            )}
            {categories.map((cat) => (
              <li
                key={cat.id}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50 group"
              >
                {renamingId === cat.id ? (
                  <>
                    <input
                      ref={renameInputRef}
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRename(cat.id);
                        if (e.key === 'Escape') setRenamingId(null);
                      }}
                      disabled={renameLoading}
                      className="flex-1 h-7 rounded border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    />
                    <button
                      type="button"
                      onClick={() => handleRename(cat.id)}
                      disabled={renameLoading}
                      className="p-1 rounded text-green-600 hover:bg-green-50 transition-colors"
                      aria-label="Save rename"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setRenamingId(null)}
                      disabled={renameLoading}
                      className="p-1 rounded text-muted-foreground hover:bg-muted transition-colors"
                      aria-label="Cancel rename"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm truncate">{cat.name}</span>
                    <button
                      type="button"
                      onClick={() => startRename(cat)}
                      className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted opacity-0 group-hover:opacity-100 transition-all"
                      aria-label={`Rename ${cat.name}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(cat.id)}
                      disabled={deletingId === cat.id}
                      className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50"
                      aria-label={`Delete ${cat.name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>

          {/* Add new category */}
          <form onSubmit={handleAdd} className="flex gap-2 pt-2 border-t">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New category name…"
              disabled={addLoading}
              className="flex-1"
            />
            <Button type="submit" size="sm" disabled={addLoading || !newName.trim()}>
              {addLoading ? '…' : 'Add'}
            </Button>
          </form>
        </div>

        <div className="flex justify-end px-6 pb-4">
          <Button variant="outline" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function ExpensesPage() {
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();

  // ── List state (server-paginated) ──────────────────────────────────────────
  // ExpensesPage owns its own query (rather than usePaginatedList) so it can read
  // BOTH the amount-sum (`total`) and the row count (`count`) from one response.
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sort, setSort] = useState('date');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editingExpense, setEditingExpense] = useState<ExpenseRow | undefined>(undefined);
  const [deletingExpense, setDeletingExpense] = useState<ExpenseRow | undefined>(undefined);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [manageCatsOpen, setManageCatsOpen] = useState(false);

  // Debounce the search term (~300ms) → fewer requests while typing.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Reset to page 1 whenever the result set changes (search / filters / sort).
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setPage(1);
  }, [debouncedQ, categoryId, from, to, sort, dir]);

  function toggleSort(key: string) {
    if (key === sort) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setDir('asc');
    }
  }

  // ── Queries ──────────────────────────────────────────────────────────────────

  const { data: catData } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => apiFetch<CategoriesResponse>('/api/expenses/categories'),
  });
  const categories = catData?.data.categories ?? [];

  const { data, isLoading } = useQuery({
    queryKey: ['expenses', { categoryId, from, to, q: debouncedQ, sort, dir, page, limit }],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      params.set('sort', sort);
      params.set('dir', dir);
      if (categoryId) params.set('category_id', categoryId);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (debouncedQ) params.set('q', debouncedQ);
      return apiFetch<ExpensesResponse>(`/api/expenses?${params.toString()}`);
    },
  });

  const expenses = data?.data.expenses ?? [];
  // `total` is the amount SUM; `count` is the matching ROW count.
  const total = data?.data.total ?? 0;
  const count = data?.data.count ?? 0;

  // ── Helpers ──────────────────────────────────────────────────────────────────

  function invalidateExpenses() {
    queryClient.invalidateQueries({ queryKey: ['expenses'] });
  }

  function invalidateCategories() {
    queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
    queryClient.invalidateQueries({ queryKey: ['expenses'] });
  }

  function openCreate() {
    setEditingExpense(undefined);
    setModalMode('create');
  }

  function openEdit(ex: ExpenseRow) {
    setEditingExpense(ex);
    setModalMode('edit');
  }

  async function handleDelete() {
    if (!deletingExpense) return;
    setDeleteLoading(true);
    try {
      await apiFetch(`/api/expenses/${deletingExpense.id}`, { method: 'DELETE' });
      toast.success('Expense deleted');
      setDeletingExpense(undefined);
      invalidateExpenses();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete expense';
      toast.error(msg);
      setDeletingExpense(undefined);
    } finally {
      setDeleteLoading(false);
    }
  }

  const colSpan = isAdmin ? 6 : 5;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">Expenses</h1>
          {!isLoading && (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {count}
            </span>
          )}
          {!isLoading && count > 0 && (
            <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2.5 py-0.5 text-xs font-medium tabular-nums">
              {formatCents(total)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons
            endpoint="/api/expenses"
            params={{
              category_id: categoryId || undefined,
              from: from || undefined,
              to: to || undefined,
              q: q || undefined,
            }}
          />
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setManageCatsOpen(true)}>
              <Settings2 className="h-4 w-4 mr-1.5" />
              Categories
            </Button>
          )}
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1.5" />
            New Expense
          </Button>
        </div>
      </div>

      {/* Filters — one line: search · date range · category (wraps on small screens) */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* 1. Search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search payee or notes…"
            className="pl-9 h-9"
          />
        </div>

        {/* 2. Date range */}
        <div className="flex items-center gap-2 text-sm shrink-0">
          <span className="text-muted-foreground">From</span>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-40 h-9 text-sm"
          />
          <span className="text-muted-foreground">To</span>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-40 h-9 text-sm"
          />
          {(from || to) && (
            <button
              type="button"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            >
              Clear
            </button>
          )}
        </div>

        {/* 3. Category */}
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="h-9 shrink-0 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <SortableTh
                label="Date"
                sortKey="date"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-28"
              />
              <th className="px-4 py-3 text-left font-medium text-muted-foreground w-36">
                Category
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden sm:table-cell">
                Payee
              </th>
              <SortableTh
                label="Amount"
                sortKey="amount"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-28 text-right"
              />
              <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">
                Notes
              </th>
              {isAdmin && (
                <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading && (
              <tr>
                <td colSpan={colSpan} className="px-4 py-10 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && expenses.length === 0 && (
              <tr>
                <td colSpan={colSpan} className="px-4 py-10 text-center text-muted-foreground">
                  No expenses found.
                </td>
              </tr>
            )}
            {expenses.map((ex) => (
              <tr key={ex.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 text-muted-foreground tabular-nums whitespace-nowrap">
                  {formatDate(ex.date)}
                </td>
                <td className="px-4 py-3">
                  {ex.category_name ? (
                    <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium">
                      {ex.category_name}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                  {ex.payee ?? '—'}
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-medium">
                  {formatCents(ex.amount)}
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden md:table-cell max-w-xs truncate">
                  {ex.notes ?? '—'}
                </td>
                {isAdmin && (
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => openEdit(ex)}
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:border-destructive/50"
                        onClick={() => setDeletingExpense(ex)}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {/* Footer total (amount SUM across the whole filtered set) */}
          {!isLoading && count > 0 && (
            <tfoot>
              <tr className="border-t bg-muted/60">
                {/* Date */}
                <td className="px-4 py-3" />
                {/* Category — label */}
                <td className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">
                  Total ({count})
                </td>
                {/* Payee (hidden sm:table-cell) */}
                <td className="px-4 py-3 hidden sm:table-cell" />
                {/* Amount */}
                <td className="px-4 py-3 text-right tabular-nums font-semibold">
                  {formatCents(total)}
                </td>
                {/* Notes (hidden md:table-cell) */}
                <td className="px-4 py-3 hidden md:table-cell" />
                {/* Actions (admin only) */}
                {isAdmin && <td className="px-4 py-3" />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && count > 0 && (
        <Pagination
          page={page}
          limit={limit}
          total={count}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Modals */}
      {modalMode && (
        <ExpenseModal
          mode={modalMode}
          expense={editingExpense}
          categories={categories}
          onClose={() => {
            setModalMode(null);
            setEditingExpense(undefined);
          }}
          onSaved={invalidateExpenses}
        />
      )}

      {deletingExpense && (
        <DeleteExpenseDialog
          expense={deletingExpense}
          onCancel={() => setDeletingExpense(undefined)}
          onConfirm={handleDelete}
          loading={deleteLoading}
        />
      )}

      {manageCatsOpen && (
        <ManageCategoriesModal
          categories={categories}
          onClose={() => setManageCatsOpen(false)}
          onChanged={invalidateCategories}
        />
      )}
    </div>
  );
}
